// Shared flex-div bar-chart primitives for the soft-completion explain
// sections. Mirrors the approved static mockup
// (soft-completion-mechanics.html): simple horizontal tracks with
// proportional segments — no chart library, matching the existing
// codebase approach (AllocationBar / ProgressBar are the same idea).
import type { ReactNode } from "react";

// Ordered categorical palette for asset classes; assignment is by
// first-seen key order via `buildColorScale`, so hues stay stable
// across every chart on the page.
export const AC_PALETTE = [
  "#3e6fa8", // blue
  "#2c8ea8", // teal
  "#58a06b", // green
  "#c29a3b", // gold
  "#9a6fb8", // purple
  "#c06b52", // rust-brown
  "#6b8e23", // olive
  "#8a6d5c", // taupe
];

// Non-categorical semantic colors shared with the mockup.
export const CHART_COLORS = {
  accent: "#0e6b5c", // kept / achieved
  over: "#b5532e", // overshoot / leftover
  pale: "#d9dfdc", // unfilled / model-wants baseline
  cash: "#9aa5a0",
};

// Stable key -> color assignment. Keys get palette colors in the order
// supplied; unknown keys fall back to the cash gray.
export function buildColorScale(keys: string[]): (key: string) => string {
  const map = new Map<string, string>();
  for (const key of keys) {
    if (!map.has(key)) map.set(key, AC_PALETTE[map.size % AC_PALETTE.length]);
  }
  return (key: string) => map.get(key) ?? CHART_COLORS.cash;
}

export interface BarSegment {
  value: number;
  color: string;
  title?: string;
}

export interface BarTick {
  value: number;
  label?: string;
}

export interface HBarRow {
  label: string;
  sub?: string;
  segments: BarSegment[];
  ticks?: BarTick[];
  note?: string;
}

const trackStyle = {
  position: "relative" as const,
  display: "flex",
  height: "15px",
  background: "var(--bs-secondary-bg, #edf0ee)",
  borderRadius: "2px",
};

function Track({
  segments,
  ticks,
  max,
}: Omit<HBarRow, "label"> & { max: number }) {
  return (
    <div style={trackStyle}>
      {segments
        .filter((s) => s.value > 0)
        .map((s, i) => (
          <div
            key={i}
            title={s.title}
            style={{
              height: "100%",
              width: `${(s.value / max) * 100}%`,
              background: s.color,
            }}
          />
        ))}
      {(ticks ?? []).map((t, i) => (
        <div
          key={`tick-${i}`}
          style={{
            position: "absolute",
            top: "-3px",
            bottom: "-3px",
            width: "2px",
            background: "var(--bs-body-color, #212529)",
            opacity: 0.6,
            left: `${(t.value / max) * 100}%`,
          }}
        >
          {t.label && (
            <span
              className="text-muted"
              style={{
                position: "absolute",
                top: "-14px",
                left: "-8px",
                fontSize: "0.6rem",
                fontWeight: 600,
                whiteSpace: "nowrap",
              }}
            >
              {t.label}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

// Horizontal bar chart: label | proportional track | monospace note.
// `max` defaults to the largest row total (including tick positions).
// `labelWidth` widens the label column for rows with long sublabels.
export function HBarChart({
  title,
  rows,
  max,
  labelWidth = 180,
}: {
  title?: string;
  rows: HBarRow[];
  max?: number;
  labelWidth?: number;
}) {
  if (rows.length === 0) return null;
  const computedMax =
    max ??
    Math.max(
      ...rows.map((r) =>
        Math.max(
          r.segments.reduce((acc, s) => acc + s.value, 0),
          ...(r.ticks ?? []).map((t) => t.value),
        ),
      ),
      1,
    );
  return (
    <ChartBox title={title}>
      {rows.map((r, i) => (
        <div
          key={`${r.label}-${i}`}
          style={{
            display: "grid",
            gridTemplateColumns: `${labelWidth}px minmax(0,1fr) 170px`,
            gap: "10px",
            alignItems: "center",
            margin: "0.28rem 0",
          }}
        >
          <div
            style={{
              fontSize: "0.76rem",
              textAlign: "right",
              lineHeight: 1.25,
            }}
          >
            {r.label}
            {r.sub && (
              <span
                className="text-muted d-block"
                style={{ fontSize: "0.66rem" }}
              >
                {r.sub}
              </span>
            )}
          </div>
          <Track segments={r.segments} ticks={r.ticks} max={computedMax} />
          <div
            className="text-muted font-monospace"
            style={{ fontSize: "0.7rem", whiteSpace: "nowrap" }}
          >
            {r.note ?? ""}
          </div>
        </div>
      ))}
    </ChartBox>
  );
}

export interface ColumnGroup {
  label: string;
  columns: { value: number; color: string; title?: string }[];
}

// Grouped column chart (per-symbol wants / after-cap / final).
// `+ 0` normalizes negative zero so payload dust (e.g. -7e-11) never
// renders as "-0.0".
export function GroupedColumnChart({
  title,
  groups,
  max,
  formatValue = (v: number) => (Number(v.toFixed(1)) + 0).toFixed(1),
}: {
  title?: string;
  groups: ColumnGroup[];
  max?: number;
  formatValue?: (v: number) => string;
}) {
  if (groups.length === 0) return null;
  const computedMax =
    max ?? Math.max(...groups.flatMap((g) => g.columns.map((c) => c.value)), 1);
  return (
    <ChartBox title={title}>
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          gap: "16px",
          height: "170px",
          paddingTop: "16px",
        }}
      >
        {groups.map((g) => (
          <div
            key={g.label}
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              height: "100%",
            }}
          >
            <div
              style={{
                flex: 1,
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "center",
                gap: "3px",
                width: "100%",
              }}
            >
              {g.columns.map((c, i) => (
                <div
                  key={i}
                  title={c.title}
                  style={{
                    width: "15px",
                    position: "relative",
                    borderRadius: "2px 2px 0 0",
                    height: `${(c.value / computedMax) * 100}%`,
                    background: c.color,
                  }}
                >
                  <span
                    className="text-muted font-monospace"
                    style={{
                      position: "absolute",
                      top: "-13px",
                      left: "50%",
                      transform: "translateX(-50%)",
                      fontSize: "0.56rem",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {formatValue(c.value)}
                  </span>
                </div>
              ))}
            </div>
            <div
              className="text-muted"
              style={{
                fontSize: "0.68rem",
                marginTop: "0.35rem",
                textAlign: "center",
              }}
            >
              {g.label}
            </div>
          </div>
        ))}
      </div>
    </ChartBox>
  );
}

export interface DivergingRow {
  label: string;
  sub?: string;
  neg: BarSegment[];
  pos: BarSegment[];
}

// Diverging bars: negative segments grow leftward from a center axis,
// positive segments grow rightward (per-account trim -> redeploy).
export function DivergingBarChart({
  title,
  rows,
  max,
}: {
  title?: string;
  rows: DivergingRow[];
  max?: number;
}) {
  if (rows.length === 0) return null;
  const computedMax =
    max ??
    Math.max(
      ...rows.map((r) =>
        Math.max(
          r.neg.reduce((acc, s) => acc + s.value, 0),
          r.pos.reduce((acc, s) => acc + s.value, 0),
        ),
      ),
      1,
    );
  const side = (segments: BarSegment[]) =>
    segments
      .filter((s) => s.value > 0)
      .map((s, i) => (
        <div
          key={i}
          title={s.title}
          style={{
            height: "100%",
            width: `${(s.value / computedMax) * 100}%`,
            background: s.color,
          }}
        />
      ));
  return (
    <ChartBox title={title}>
      {rows.map((r, i) => (
        <div
          key={`${r.label}-${i}`}
          style={{
            display: "grid",
            gridTemplateColumns: "160px minmax(0,1fr)",
            gap: "10px",
            alignItems: "center",
            margin: "0.28rem 0",
          }}
        >
          <div style={{ fontSize: "0.76rem", textAlign: "right" }}>
            {r.label}
            {r.sub && (
              <span
                className="text-muted d-block"
                style={{ fontSize: "0.66rem" }}
              >
                {r.sub}
              </span>
            )}
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              height: "15px",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                borderRight: "2px solid var(--bs-body-color, #212529)",
              }}
            >
              {side(r.neg)}
            </div>
            <div style={{ display: "flex" }}>{side(r.pos)}</div>
          </div>
        </div>
      ))}
    </ChartBox>
  );
}

export interface SignedDeviationRow {
  label: string;
  // Signed value in the chart unit (e.g. percentage points).
  value: number;
  color: string;
}

// Center-zero signed bars (verification deviations).
export function SignedDeviationChart({
  title,
  rows,
  max,
  formatValue = (v: number) => `${v > 0 ? "+" : ""}${v.toFixed(1)}pp`,
}: {
  title?: string;
  rows: SignedDeviationRow[];
  max?: number;
  formatValue?: (v: number) => string;
}) {
  if (rows.length === 0) return null;
  const computedMax = max ?? Math.max(...rows.map((r) => Math.abs(r.value)), 1);
  return (
    <ChartBox title={title}>
      {rows.map((r, i) => (
        <div
          key={`${r.label}-${i}`}
          style={{
            display: "grid",
            gridTemplateColumns: "200px minmax(0,1fr) 90px",
            gap: "10px",
            alignItems: "center",
            margin: "0.26rem 0",
          }}
        >
          <div style={{ fontSize: "0.76rem", textAlign: "right" }}>
            {r.label}
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              height: "15px",
              background: "var(--bs-secondary-bg, #edf0ee)",
              borderRadius: "2px",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                borderRight: "2px solid var(--bs-body-color, #212529)",
              }}
            >
              {r.value < 0 && (
                <div
                  style={{
                    height: "100%",
                    width: `${(Math.abs(r.value) / computedMax) * 100}%`,
                    background: r.color,
                  }}
                />
              )}
            </div>
            <div style={{ display: "flex" }}>
              {r.value > 0 && (
                <div
                  style={{
                    height: "100%",
                    width: `${(r.value / computedMax) * 100}%`,
                    background: r.color,
                  }}
                />
              )}
            </div>
          </div>
          <div
            className="text-muted font-monospace"
            style={{ fontSize: "0.7rem" }}
          >
            {formatValue(r.value)}
          </div>
        </div>
      ))}
    </ChartBox>
  );
}

// Legend of colored swatches.
export function ChartLegend({
  items,
}: {
  items: { label: string; color: string }[];
}) {
  if (items.length === 0) return null;
  return (
    <div
      className="text-muted"
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: "0.4rem 1rem",
        marginTop: "0.6rem",
        fontSize: "0.7rem",
      }}
    >
      {items.map((it) => (
        <span key={it.label}>
          <span
            style={{
              display: "inline-block",
              width: "10px",
              height: "10px",
              marginRight: "0.3rem",
              verticalAlign: "-1px",
              borderRadius: "2px",
              background: it.color,
            }}
          />
          {it.label}
        </span>
      ))}
    </div>
  );
}

// Bordered chart container with an uppercase caption, matching the
// mockup's `.chart` boxes and the table-caption register.
export function ChartBox({
  title,
  children,
}: {
  title?: string;
  children: ReactNode;
}) {
  return (
    <div
      className="border rounded my-2"
      style={{ padding: "0.8rem 0.95rem 0.9rem" }}
    >
      {title && (
        <p
          className="text-muted text-uppercase fw-bold"
          style={{
            fontSize: "0.7rem",
            letterSpacing: "0.05em",
            margin: "0 0 0.65rem",
          }}
        >
          {title}
        </p>
      )}
      {children}
    </div>
  );
}
